// Local wrapper around the vendored wizard form (see ~/vendor/ra-form-layout)
import clsx from 'clsx'
import * as React from 'react'
import { KeyboardEvent, useEffect, useState } from 'react'
import { CardContentInner, Form } from 'react-admin'
import { useFormState } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { WizardProgress, WizardToolbar } from '~/vendor/ra-form-layout'
import type { WizardFormStepProps } from '~/vendor/ra-form-layout/WizardFormStep'

/**
 * A wizard form: one step visible at a time, with a stepper and a toolbar.
 *
 * Alternative to <SimpleForm>, to be used as a child of <Create> or <Edit>.
 * Expects <WizardFormStep> elements as children.
 *
 * Rewritten for react-admin 5. The previous version was built on
 * <FormWithRedirect> and react-final-form, both of which react-admin removed in
 * v4:
 *
 *   - <FormWithRedirect render={...}> becomes <Form>, which sets up
 *     react-hook-form's FormProvider and takes children rather than a render
 *     prop.
 *   - `invalid` / `pristine` / `saving` came in as render-prop arguments;
 *     they now come from useFormState().
 *   - `handleSubmitWithRedirect` no longer exists. Redirection is configured on
 *     <Create>/<Edit>, and the toolbar's <SaveButton> submits the form itself,
 *     so the toolbar contract keeps the prop but leaves it undefined.
 *   - useRouteMatch/useHistory became useLocation/useNavigate.
 */
const WizardForm = ({
  children,
  toolbar = WizardToolbar,
  progress = WizardProgress,
  formRootPathname,
  syncWithLocation = true,
  ignoreKeyPress,
  className,
  setSaveProps,
  ...rest
}: any) => (
  <Form {...rest}>
    <WizardFormView
      toolbar={toolbar}
      progress={progress}
      formRootPathname={formRootPathname}
      syncWithLocation={syncWithLocation}
      ignoreKeyPress={ignoreKeyPress}
      className={className}
      setSaveProps={setSaveProps}
    >
      {children}
    </WizardFormView>
  </Form>
)

const WizardFormView = ({
  children,
  className,
  toolbar,
  progress,
  formRootPathname,
  syncWithLocation,
  ignoreKeyPress,
  setSaveProps,
}: any) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { isValid, isDirty, isSubmitting } = useFormState()
  const invalid = !isValid

  // The step index is the trailing path segment, when there is one.
  const routeStep = React.useMemo(() => {
    if (!syncWithLocation || !formRootPathname) return 0
    const tail = pathname.slice(formRootPathname.length).replace(/^\//, '')
    const parsed = Number.parseInt(tail, 10)
    return Number.isFinite(parsed) ? parsed : 0
  }, [pathname, formRootPathname, syncWithLocation])

  const [currentStep, setCurrentStep] = useState<number>(routeStep)
  const [dirtySteps, setDirtySteps] = useState<number[]>([])

  useEffect(() => {
    if (!syncWithLocation || routeStep === currentStep) return
    navigate(currentStep ? `${formRootPathname}/${currentStep}` : formRootPathname)
  }, [currentStep, formRootPathname, navigate, routeStep, syncWithLocation])

  // Keep track of visited steps while the form is dirty, so a step the user has
  // touched stays mounted and keeps its values.
  useEffect(() => {
    if (!isDirty) {
      if (dirtySteps.length > 0) setDirtySteps([])
      return
    }
    if (!dirtySteps.includes(currentStep)) setDirtySteps((visited) => [...visited, currentStep])
  }, [currentStep, isDirty, dirtySteps])

  const steps = React.Children.toArray(children)
  const hasPreviousStep = currentStep > 0
  const hasNextStep = currentStep < steps.length - 1

  const handleNext = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault()
    setCurrentStep((step) => step + 1)
  }
  const handlePrevious = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault()
    setCurrentStep((step) => step - 1)
  }
  // Only backwards navigation is offered by the stepper, so no extra checks.
  const handleIndexNavigation = (index: number): void => setCurrentStep(index)

  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Enter') return
    const target = event.target as HTMLElement
    if (target?.tagName === 'BUTTON') return
    // Enter must never submit a half-finished wizard.
    event.preventDefault()
    if (hasNextStep) setCurrentStep((step) => step + 1)
  }

  useEffect(() => {
    if (!setSaveProps) return
    setSaveProps({ invalid, saving: isSubmitting, pristine: !isDirty })
  }, [invalid, isSubmitting, isDirty, setSaveProps])

  return (
    <>
      {React.createElement(progress, {
        steps,
        currentStep,
        onStepClick: handleIndexNavigation,
        invalid,
      })}
      <div
        className={clsx('wizard-form', className)}
        onKeyPress={ignoreKeyPress ? undefined : handleKeyPress}
      >
        <CardContentInner>
          {React.Children.map(children, (step, index) =>
            React.isValidElement(step) && (index === currentStep || dirtySteps.includes(index)) ? (
              <span style={currentStep !== index ? { display: 'none' } : {}}>{step}</span>
            ) : null
          )}
        </CardContentInner>
        {toolbar &&
          React.createElement(toolbar, {
            hasNextStep,
            hasPreviousStep,
            onNextClick: handleNext,
            onPreviousClick: handlePrevious,
            invalid,
            saving: isSubmitting,
            pristine: !isDirty,
          })}
      </div>
    </>
  )
}

export interface WizardToolbarProps {
  hasPreviousStep?: boolean
  hasNextStep?: boolean
  onPreviousClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  onNextClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  invalid?: boolean
  saving?: boolean
  pristine?: boolean
  /** Kept for the existing toolbar signature; react-admin 5 has no equivalent. */
  handleSubmit?: () => void
  handleSubmitWithRedirect?: () => void
  redirect?: any
  submitOnEnter?: boolean
}

export interface WizardFormProgressProps {
  currentStep?: number
  onStepClick?: (index: number) => void
  steps?: React.ReactElement<WizardFormStepProps>[]
  invalid?: boolean
}

export default WizardForm
